import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { message, Slider, UploadFile } from 'antd';
import { StyledCropperModal } from './style';
import { useIntl } from 'react-intl';
import {
  PlusOutlined,
  MinusOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from '@ant-design/icons';

interface CropperModalProps {
  image: string;
  onCancel: () => void;
  onCropComplete: (file: File) => void;
  visible: boolean;
  file: UploadFile;
  aspectRatio?: number;
}

interface Crop {
  x: number;
  y: number;
}

const ZOOM_STEP = 0.05;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const ROTATION_STEP = 5;
const MIN_ROTATION = 0;
const MAX_ROTATION = 360;

export const CropperModal: React.FC<CropperModalProps> = ({
  image,
  onCancel,
  onCropComplete,
  visible,
  file,
  aspectRatio,
}) => {
  const [crop, setCrop] = useState<Crop>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [rotation, setRotation] = useState<number>(0);
  const intl = useIntl();

  const onCropDone = useCallback(async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        image,
        croppedAreaPixels,
        file,
        rotation
      );
      onCropComplete(croppedImage);
    } catch (err) {
      message.error(intl.formatMessage({ id: 'error_images_aggregate' }));
    }
  }, [croppedAreaPixels, image, onCropComplete, rotation]);

  return (
    <StyledCropperModal open={visible} onCancel={onCancel} onOk={onCropDone}>
      <div className={'cropper-modal-title'}>
        {intl.formatMessage({ id: 'gallery.edit_image' })}
      </div>

      <div className={'cropper-modal-container'}>
        <Cropper
          aspect={aspectRatio || 1}
          image={image}
          crop={crop}
          zoom={zoom}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={(_, croppedPixels) =>
            setCroppedAreaPixels(croppedPixels)
          }
          rotation={rotation}
        />
      </div>

      <div className={'cropper-maodal-slider-wrapper'}>
        <MinusOutlined
          onClick={() =>
            setZoom((prev) =>
              Math.max(MIN_ZOOM, parseFloat((prev - ZOOM_STEP).toFixed(2)))
            )
          }
        />
        <Slider
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={ZOOM_STEP}
          value={zoom}
          onChange={(value: number) => setZoom(value)}
          style={{ flex: 1 }}
        />
        <PlusOutlined
          onClick={() =>
            setZoom((prev) =>
              Math.min(MAX_ZOOM, parseFloat((prev + ZOOM_STEP).toFixed(2)))
            )
          }
        />
      </div>

      <div className={'cropper-maodal-slider-wrapper'}>
        <RotateLeftOutlined
          onClick={() =>
            setRotation(
              (prev) => (prev - ROTATION_STEP + MAX_ROTATION) % MAX_ROTATION
            )
          }
        />
        <Slider
          min={MIN_ROTATION}
          max={MAX_ROTATION}
          step={ROTATION_STEP}
          value={rotation}
          tooltip={{ formatter: (v) => `${v}°` }}
          onChange={(value: number) => setRotation(value)}
          style={{ flex: 1 }}
        />
        <RotateRightOutlined
          onClick={() =>
            setRotation((prev) => (prev + ROTATION_STEP) % MAX_ROTATION)
          }
        />
      </div>
    </StyledCropperModal>
  );
};

export async function getCroppedImg(
  imageSrc: string,
  crop: Area,
  originalFile: UploadFile,
  rotation = 0
): Promise<File> {
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });

  const image = await createImage(imageSrc);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const rotRad = (rotation * Math.PI) / 180;

  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  if (!ctx) throw new Error('Canvas context not available');

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = crop.width;
  croppedCanvas.height = crop.height;
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) throw new Error('Cropped canvas context not available');

  croppedCtx.drawImage(
    canvas,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise<File>((resolve, reject) => {
    croppedCanvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Canvas is empty'));
      resolve(
        new File([blob], originalFile.name, {
          type: originalFile.originFileObj?.type || 'image/jpeg',
        })
      );
    }, originalFile.originFileObj?.type || 'image/jpeg');
  });
}
